/**
 * L'APPEL À L'API. Une fonction, un jeton, et des codes d'erreur qui restent des codes.
 *
 * LE JETON VIT DANS `localStorage`. Ce n'est pas le choix le plus sûr dans l'absolu — un cookie
 * `HttpOnly` résisterait mieux à un script injecté — mais c'est celui que le serveur attend
 * aujourd'hui (`x-session-token`, voir `middleware/session.ts`), et faire semblant côté front d'une
 * sécurité que le serveur ne pose pas serait pire que de l'écrire ici. Le jour où l'API passera au
 * cookie, ce fichier est le seul à changer.
 *
 * CE QUI NE SE FAIT PAS ICI : traduire une erreur. `ErreurApi` porte le CODE ; c'est `lexique/`
 * qui le met dans la langue du lecteur, et c'est ce qui a permis l'anglais sans toucher aux appels.
 *
 * UN RÉSEAU COUPÉ EST UNE ERREUR COMME UNE AUTRE, avec son code : `HORS_LIGNE`. Sans lui, un
 * `fetch` qui échoue remontait un `TypeError` du navigateur, que l'écran traduisait par « une
 * erreur est survenue » — la seule panne dont le joueur connaît DÉJÀ la cause était aussi la seule
 * qu'on ne savait pas nommer.
 */

import { serveurInjoignable, serveurJoignable } from "../reseau.js";

const CLEF_JETON = "destinia.jeton";

export class ErreurApi extends Error {
  constructor(
    readonly code: string,
    readonly statut: number
  ) {
    super(code);
    this.name = "ErreurApi";
  }
}

let jeton: string | null = lisLeJeton();

function lisLeJeton(): string | null {
  try {
    return localStorage.getItem(CLEF_JETON);
  } catch {
    // Navigation privée, stockage refusé : on joue quand même, on se reconnectera au rechargement.
    return null;
  }
}

export function leJeton(): string | null {
  return jeton;
}

export function poseLeJeton(valeur: string | null): void {
  jeton = valeur;
  try {
    if (valeur) localStorage.setItem(CLEF_JETON, valeur);
    else localStorage.removeItem(CLEF_JETON);
  } catch {
    /* même raison */
  }
}

type Methode = "GET" | "POST" | "PATCH" | "DELETE";

async function appelle<T>(methode: Methode, chemin: string, corps?: unknown): Promise<T> {
  const entetes: Record<string, string> = {};
  if (corps !== undefined) entetes["content-type"] = "application/json";
  if (jeton) entetes["x-session-token"] = jeton;

  let reponse: Response;
  try {
    reponse = await fetch(`/api${chemin}`, {
      method: methode,
      headers: entetes,
      body: corps === undefined ? undefined : JSON.stringify(corps)
    });
  } catch {
    // Pas de réponse HTTP du tout : le serveur n'est pas joignable (§14). C'est la seule preuve
    // qui vaille — `navigator.onLine` dit du réseau, pas du serveur.
    serveurInjoignable();
    throw new ErreurApi("HORS_LIGNE", 0);
  }
  // Un refus prouve que le serveur est là, ce qui est tout ce qu'on demande ici.
  serveurJoignable();

  if (reponse.status === 204) return undefined as T;

  const charge = (await reponse.json().catch(() => ({}))) as Record<string, unknown>;
  if (!reponse.ok) {
    const code = typeof charge.code === "string" ? charge.code : "ERREUR_INCONNUE";
    // Un jeton expiré ou révoqué : on l'oublie tout de suite, sinon chaque écran suivant reposera
    // la même question au serveur et recevra le même non.
    if (reponse.status === 401) poseLeJeton(null);
    throw new ErreurApi(code, reponse.status);
  }
  return charge as T;
}

export const api = {
  lis: <T>(chemin: string) => appelle<T>("GET", chemin),
  poste: <T>(chemin: string, corps?: unknown) => appelle<T>("POST", chemin, corps),
  /** Les annotations de carte (§4.2) se modifient et s'effacent : deux verbes de plus, et les mêmes
   * codes d'erreur. On ne les ajoute pas « au cas où » — `PATCH` et `DELETE` ont chacun un appelant. */
  envoie: <T>(methode: "POST" | "PATCH" | "DELETE", chemin: string, corps?: unknown) =>
    appelle<T>(methode, chemin, corps)
};
